/**
 * ★ [E-72, sobre o E-64] Os ARQUIVOS que o agente entregou na conversa —
 * a metade PERSISTIDA do cartão de download.
 *
 * ⚠️ **Não confundir com `store/artefato.js`**, que é vizinho e tem nome
 * parecido: aquele guarda o painel de CARTÃO DE DADO (`ui.component` de uma
 * tool, o aside da visão imersiva) e a lista do índice "Artefatos desta
 * conversa". Este aqui é ARQUIVO de verdade — `.xlsx`, `.pdf`, `.py` — que o
 * agente gravou no workspace e o backend entrega em base64.
 *
 * ## Por que um slice, e não mais um campo em `state.js`
 *
 * Mesmo movimento de `artefato.js`/`toolParts.js`: slice autocontido,
 * composto no `store/index.js`. Ele NÃO é despachado pelo motor (`apiChat`
 * por `storeNamespace`), então não precisa existir nas três superfícies de
 * chat — quem o despacha é a bolha do Modo Agente, que só roda no namespace
 * principal — e não obriga a tocar `state/actions/getters/mutations.js`, os
 * quatro arquivos sob edição concorrente permanente.
 *
 * ## As duas metades, e por que a persistida existe
 *
 * - **AO VIVO:** o evento SSE `arquivo` cai em `arquivoStreaming`
 *   (`state.js`), canal transiente zerado a cada turno. Ele desenha o cartão
 *   enquanto o turno roda.
 * - **PERSISTIDA (aqui):** `GET /conversas/{id}/artefatos`. Sem ela o cartão
 *   SOME no F5 — que foi exatamente o MUST-1 do cartão de fonte web, já pago
 *   uma vez nesta base.
 *
 * ## `idConversa` guardado JUNTO com a lista
 *
 * A lista é de UMA conversa. Guardar o `idConversa` de origem ao lado dela é
 * o que impede o artefato da conversa A de aparecer na conversa B durante a
 * janela entre trocar de conversa e a resposta nova chegar: quem lê compara,
 * e uma lista de outra conversa é tratada como ausente — não como dado.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { apiArtefatosConversa } from '@modules/aawz-intelligence-v2/api/apiArtefatosConversa'

export function criarStateArtefatosDoWorkspace() {
  return {
    // `[{id_objeto, id_run, nome, content_type, bytes, versao, tipo}]` — o
    // que `GET /conversas/{id}/artefatos` devolveu para `awIdConversa`.
    artefatosDoWorkspace: [],
    // De QUAL conversa é a lista acima. `null` = nunca carregou.
    artefatosDoWorkspaceIdConversa: null,
    artefatosDoWorkspaceCarregando: false,
  }
}

export const artefatosDoWorkspaceMutations = {
  SET_ARTEFATOS_DO_WORKSPACE(state, payload) {
    const dados = payload || {}
    state.artefatosDoWorkspace = Array.isArray(dados.artefatos) ? dados.artefatos : []
    state.artefatosDoWorkspaceIdConversa = dados.idConversa || null
  },
  SET_ARTEFATOS_DO_WORKSPACE_CARREGANDO(state, payload) {
    state.artefatosDoWorkspaceCarregando = Boolean(payload)
  },
  LIMPAR_ARTEFATOS_DO_WORKSPACE(state) {
    state.artefatosDoWorkspace = []
    state.artefatosDoWorkspaceIdConversa = null
    state.artefatosDoWorkspaceCarregando = false
  },
}

export const artefatosDoWorkspaceGetters = {
  artefatosDoWorkspace: (state) => state.artefatosDoWorkspace || [],
  artefatosDoWorkspaceIdConversa: (state) => state.artefatosDoWorkspaceIdConversa || null,
  artefatosDoWorkspaceCarregando: (state) => Boolean(state.artefatosDoWorkspaceCarregando),
}

export const artefatosDoWorkspaceActions = {
  /**
   * Carrega (uma vez por conversa) os arquivos entregues.
   *
   * Despachada pela BOLHA, não pela view: cada bolha de assistente de um run
   * do Modo Agente pede a lista ao montar, e o guard abaixo faz disso UMA
   * requisição. Pôr o disparo na view obrigaria `view-chat.vue`,
   * `view-chat-imersivo.vue` e o `chat-ia` de página cheia a lembrarem de
   * chamá-lo — três lugares para esquecer um.
   *
   * O guard é por `idConversa` E por `carregando`: a mesma conversa não
   * recarrega, e N bolhas montando no mesmo tick geram uma chamada só.
   * `forcar: true` fura o guard (usado no fim do turno, quando o run
   * acabou de gravar arquivo novo).
   *
   * @param {Object} params `{idConversa, forcar}`.
   * @returns {Promise<Object|null>} envelope da API, ou `null` quando o
   *   guard barrou (nada foi pedido).
   */
  async carregarArtefatosDoWorkspace({ commit, state }, { idConversa, forcar } = {}) {
    if (!idConversa) return null
    if (state.artefatosDoWorkspaceCarregando) return null
    if (!forcar && state.artefatosDoWorkspaceIdConversa === idConversa) return null

    commit('SET_ARTEFATOS_DO_WORKSPACE_CARREGANDO', true)
    try {
      const r = await apiArtefatosConversa.listar({ idConversa: idConversa })
      // Falha NÃO grava `idConversa`: sem isso o guard trataria a conversa
      // como "já carregada, e vazia" e nunca mais tentaria — o consultor
      // ficaria sem o arquivo por causa de um 500 momentâneo.
      if (!r || r.status !== 200 || !r.value) return r
      commit('SET_ARTEFATOS_DO_WORKSPACE', {
        idConversa: idConversa,
        artefatos: r.value.artefatos || [],
      })
      return r
    } finally {
      commit('SET_ARTEFATOS_DO_WORKSPACE_CARREGANDO', false)
    }
  },

  limparArtefatosDoWorkspace({ commit }) {
    commit('LIMPAR_ARTEFATOS_DO_WORKSPACE')
  },
}

// Módulo autocontido, para quem preferir registrar o slice namespaced em vez
// de compor no namespace raiz. `state` é FUNÇÃO — mesma disciplina de
// `artefato.js`/`toolParts.js`: dois registros nunca compartilham a mesma
// referência de objeto.
export default {
  namespaced: true,
  state: criarStateArtefatosDoWorkspace,
  getters: artefatosDoWorkspaceGetters,
  mutations: artefatosDoWorkspaceMutations,
  actions: artefatosDoWorkspaceActions,
}
